<script lang="ts">
	import { getPriceForProductForCustomerOfSize } from "../../../../core/schema/utils/getPriceForProductForCustomerOfSize.js";
	import { isProductForCustomerConfigurable } from "../../../../core/schema/utils/isProductForCustomerConfigurable.js";
	import type { OrderProductOptions } from "../../../../core/schema/OrderProductOptions.js";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import { onMount } from "svelte";
	import type { ProductSize } from "../../../../core/schema/ProductSize.js";
	import { doesProductForCustomerHaveMultipleSizes } from "../../../../core/schema/utils/doesProductForCustomerHaveMultipleSizes.js";
	import { cartPathMap } from "../../../../core/schema/paths/cartPathMap.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import Button from "../Button.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { reportAddedProductToCart, reportProductDetail } from "../../UniversalAnalytics.js";
	import { emptyOptions } from "../../../../core/schema/utils/emptyOptions.js";
	import Form from "../Form/Form.svelte";
	import type { ProductAmountPerCategory } from "../../../../core/schema/ProductAmountPerCategory.js";
	import { getFreeSlotsForProduct } from "../../../../core/schema/utils/getFreeSlotsForProduct.js";
	import { getCartStore } from "../../contexts/cartStoreContextKey.js";
	import { getDefaultSizeForProductForCustomer } from "../../../../core/schema/utils/getDefaultSizeForProductForCustomer.js";
	import { inflectProductSizeLabelType } from "../../../../core/schema/names/inflectProductSizeLabelType.js";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";
	import { formatNumber } from "../../../../core/utils/formatNumber.js";
	import { shouldShowProductForCustomerSize } from "../../../../core/schema/utils/shouldShowProductForCustomerSize.js";
	import { isProductCurrentlyPurchasable } from "../../../../core/schema/utils/isProductPurchasable.js";
	import { sizeLabelsWithSize } from "../../../../core/schema/sizeLabelsWithSize.js";
	import { ProductOption, productOptionKeyMap } from "../../../../core/schema/ProductOption.js";
	import type { OpenableOption } from "../Configurator/OpenableOption.js";
	import { isSimpleToggleProductOption } from "../Configurator/isSimpleToggleProductOption.js";
	import { getPriceOfOrderProduct } from "../../../../core/schema/utils/getPriceOfOrderProduct.js";
	import { transformProductToOrderProduct } from "../../../../core/schema/utils/transformProductToOrderProduct.js";
	import { minimalOptions } from "../../../../core/schema/productOptionPrices.js";
	import { CustomerType } from "../../../../core/schema/CustomerType.js";
	import { isProductOptionUsed } from "../../../../core/schema/utils/isProductOptionUsed.js";
	import { requiredProductOptions } from "../../../../core/schema/requiredProductOptions.js";
	import ProductCartFormNearestDelivery from "./ProductCartFormNearestDelivery.svelte";
	import HoverTooltip from "../HoverTooltip.svelte";
	import ExpressProductWarningModal from "./ExpressProductWarningModal.svelte";

	export let product: ProductForCustomer;
	export let maxProductsPerCategory: ProductAmountPerCategory;
	export let selectedSize = getDefaultSizeForProductForCustomer(product);
	export let enabledOption: ProductOption | undefined = undefined;

	const cartStore = getCartStore();
	const { TENANT_LOCALE } = getGlobalSettings();

	let options = structuredClone(emptyOptions);
	let isExpress = false;
	let showExpressWarningModal = false;

	const putItemToCart = createPromiseStore(
		async (product: ProductForCustomer, quantity: number, size: ProductSize, options?: OrderProductOptions) => {
			const response = await cartStore.addProduct(
				product,
				quantity,
				product.minimumOrderQuantity,
				size,
				isExpress,
				options,
			);
			reportAddedProductToCart(response.item, TENANT_LOCALE);
			return response;
		},
	);

	$: allowExpress = product.warehouseCounts !== undefined;
	let configurationDrawerOpen = false;
	let configuratorOpenedOption: OpenableOption | undefined;

	let alreadyMounted = false;
	onMount(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const size = searchParams.get("size") as ProductSize | null;
		if (size !== null) {
			if (product.availableSizes.includes(size)) {
				selectedSize = size;
			}
		}
		if (searchParams.has("config")) {
			configurationDrawerOpen = true;
		}

		isExpress = searchParams.has("express");

		alreadyMounted = true;

		reportProductDetail(product, TENANT_LOCALE, selectedSize);
	});
	$: {
		if (doesProductForCustomerHaveMultipleSizes(product) && alreadyMounted) {
			const searchParams = new URLSearchParams(window.location.search);
			const size = searchParams.get("size");
			if (size !== selectedSize) {
				searchParams.set("size", selectedSize);
				window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
			}
		}
	}

	function getPriceForSizeAndOption(size: ProductSize, enabledOption: ProductOption | undefined): number {
		return getPriceOfOrderProduct(
			transformProductToOrderProduct(
				crypto.randomUUID(),
				product,
				1,
				size,
				enabledOption !== undefined && !isSimpleToggleProductOption(enabledOption)
					? {
							...options,
							[productOptionKeyMap[enabledOption]]: minimalOptions[productOptionKeyMap[enabledOption]],
						}
					: options,
			),
		).withTax;
	}

	function onEnabledOptionChanged(enabledOption: ProductOption | undefined): void {
		if (enabledOption === undefined) {
			return;
		}
		if (isSimpleToggleProductOption(enabledOption)) {
			options = {
				...options,
				[productOptionKeyMap[enabledOption]]: true,
			};
		} else if (!isProductOptionUsed(enabledOption, options[productOptionKeyMap[enabledOption]])) {
			if (enabledOption === ProductOption.LyingPhoto || enabledOption === ProductOption.StandingPhotos) {
				configuratorOpenedOption = "photos";
			} else {
				configuratorOpenedOption = enabledOption;
			}
		}
	}
	$: onEnabledOptionChanged(enabledOption);

	async function addItem(): Promise<void> {
		for (const enabledOption of product.enabledOptions) {
			if (requiredProductOptions.includes(enabledOption)) {
				if (!isProductOptionUsed(enabledOption, options[productOptionKeyMap[enabledOption]])) {
					configurationDrawerOpen = true;
					configuratorOpenedOption =
						enabledOption === ProductOption.StandingPhotos || enabledOption === ProductOption.LyingPhoto
							? "photos"
							: enabledOption;
					return;
				}
			}
		}
		if (enabledOption !== undefined && !isSimpleToggleProductOption(enabledOption)) {
			configurationDrawerOpen = true;
			return;
		}
		await putItemToCart.call(
			product,
			1,
			selectedSize,
			isProductForCustomerConfigurable(product, false, selectedSize) ? options : undefined,
		);
		window.location.assign(`/${cartPathMap[TENANT_LOCALE]}`);
	}

	async function submit(): Promise<void> {
		if ($cartStore.value.order.isExpress && !isExpress) {
			showExpressWarningModal = true;
		} else {
			await addItem();
		}
	}

	async function saveConfiguratorOptions({ detail }: CustomEvent<OrderProductOptions>): Promise<void> {
		await putItemToCart.call(product, 1, selectedSize, detail);
		window.location.assign(`/${cartPathMap[TENANT_LOCALE]}`);
	}

	function selectSize(size: string): void {
		selectedSize = size as ProductSize;
		reportProductDetail(product, TENANT_LOCALE, selectedSize);
	}

	const heightClasses = ["h-[1.5rem]", "h-[1.625rem]", "h-[1.75rem]", "h-[2rem]"];
	const widthClasses = ["w-[1.5rem]", "w-[1.625rem]", "w-[1.75rem]", "w-[2rem]"];

	$: canAddToCart =
		getFreeSlotsForProduct(product, maxProductsPerCategory, $cartStore.value.order.products) >=
			product.minimumOrderQuantity &&
		isProductCurrentlyPurchasable(product, CustomerType.B2c, isExpress) &&
		(!isExpress || (product.warehouseCounts?.[selectedSize] ?? 0) > 0) &&
		(!product.soldOut || $cartStore.value.properties.canAddSoldOutExpressProduct);

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */

	const loadProductConfigurator = createPromiseStore(() => import("../Configurator/ProductConfigurator.svelte"));

	$: {
		if (configurationDrawerOpen && $loadProductConfigurator.status === "initial") {
			void loadProductConfigurator.call();
		}
	}
</script>

<ExpressProductWarningModal {product} {showExpressWarningModal} {addItem} />

{#if $loadProductConfigurator.status === "success" && canAddToCart}
	{@const ProductConfigurator = $loadProductConfigurator.result.default}
	<ProductConfigurator
		bind:isExpress
		{allowExpress}
		bind:isOpen={configurationDrawerOpen}
		{product}
		size={selectedSize}
		{options}
		on:saveOptions={saveConfiguratorOptions}
		bind:openedOption={configuratorOpenedOption}
	/>
{/if}

<Form on:submit={submit} class="mt-8">
	{#if shouldShowProductForCustomerSize(product)}
		{#if product.availableSizes.length > 1}
			<span class="mb-3 font-semibold leading-7">Vyberte si velikost</span>
		{/if}
		<div class="flex flex-col gap-2">
			{#each product.availableSizes as size, i}
				{@const selected = selectedSize === size}
				{@const disabled = isExpress && (product.warehouseCounts?.[size] ?? 0) < 1}
				<label
					for="size-{size}"
					class="group relative flex w-full cursor-pointer justify-between rounded-lg border py-[0.875rem] pl-3 pr-6 {selected
						? 'outline-secondary border-transparent outline outline-2'
						: ''}"
					class:bg-gray-100={disabled}
					class:!cursor-not-allowed={disabled}
				>
					<span class="relative flex items-center gap-3">
						<input
							bind:group={selectedSize}
							on:click={() => {
								selectSize(size);
							}}
							class="custom-radio"
							id="size-{size}"
							name="size"
							type="radio"
							value={size}
							{disabled}
							required
						/>
						{#if product.sizeLabel && sizeLabelsWithSize.includes(product.sizeLabel.type)}
							<label for="size-{size}" class="{heightClasses[i]} {widthClasses[i]} uppercase">{size}</label>
						{/if}
						{#if product.sizeLabel}
							{@const value = product.sizeLabel.values[productSizeKeyMap[size]]}
							<span class="leading-6 {selectedSize === size ? 'text-primary font-semibold' : ''}">
								{formatNumber(value, TENANT_LOCALE, false)}
								{inflectProductSizeLabelType[product.sizeLabel.type](value)}
							</span>
						{/if}
					</span>
					<span class="text-md font-semibold leading-7 {selectedSize === size ? 'text-primary' : ''}">
						{nbspify(formatPrice(getPriceForSizeAndOption(size, enabledOption), TENANT_LOCALE, false))}
					</span>
					{#if disabled}
						<HoverTooltip>
							<p>Tato velikost není dostupná pro expresní objednávku</p>
						</HoverTooltip>
					{/if}
				</label>
			{/each}
		</div>
	{:else}
		{@const defaultPrice = getPriceForProductForCustomerOfSize(product, getDefaultSizeForProductForCustomer(product))}
		<div class="flex flex-col items-end gap-4 lg:justify-end">
			<span class="text-right text-xl font-semibold leading-7">
				{nbspify(formatPrice(defaultPrice?.withTax ?? 0, TENANT_LOCALE, false))}
			</span>
			<div class="text-secondary flex items-end gap-2 text-[.8rem] leading-[1] opacity-60">
				bez DPH
				<span class="text-right text-base font-semibold">
					{nbspify(formatPrice(defaultPrice?.withoutTax ?? 0, TENANT_LOCALE, false))}
				</span>
			</div>
		</div>
	{/if}

	{#if product.minimumOrderQuantity > 1}
		<p class="text-secondary text-sm font-semibold">
			Minimální objednávka: {product.minimumOrderQuantity} ks
		</p>
	{/if}
	<div class="mt-6 flex flex-col gap-2 sm:flex-row-reverse md:flex-col lg:mt-10 xl:flex-row-reverse">
		<Button
			type="submit"
			class="w-full justify-center !rounded-[1.875rem] py-[1.063rem] text-[1.125rem] leading-6 md:text-[1.375rem]"
			variant="primary"
			icon={cake}
			iconClass="w-6"
			disabled={$putItemToCart.status === "loading" || !canAddToCart}
		>
			Přidat do krabice
		</Button>
		{#if isProductForCustomerConfigurable(product, false, selectedSize)}
			<Button
				class="w-full justify-center !rounded-[1.875rem] py-[1.063rem] text-[1.125rem] leading-6 md:text-[1.375rem]"
				type="button"
				variant="secondary"
				on:click={() => {
					configurationDrawerOpen = true;
				}}
				disabled={$loadProductConfigurator.status === "loading" || !canAddToCart}
			>
				{#if $loadProductConfigurator.status === "loading"}
					Načítám…
				{:else}
					Upravit
				{/if}
			</Button>
		{/if}
	</div>
	<div class="mt-8 lg:mt-10">
		<ProductCartFormNearestDelivery {product} {allowExpress} bind:isExpress />
	</div>
</Form>
